import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { getBlogFAQs } from '../data/blogFAQs';

interface FAQItem {
  question: string;
  answer: string;
}

interface BlogFAQProps {
  slug?: string;
}

const BlogFAQ: React.FC<BlogFAQProps> = ({ slug }) => {
  const { t, i18n } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // ✅ Récupérer les FAQs quand le slug ou la langue change
  useEffect(() => {
    if (slug) {
      const currentLocale = i18n.language; // 'fr', 'en', ou 'ar'
      const items = getBlogFAQs(slug, currentLocale);
      setFaqItems(items);
    } else {
      setFaqItems([]);
    }
  }, [slug, i18n.language]);

  // ✅ Détecter si la langue est l'arabe pour le RTL
  const isRTL = i18n.language === 'ar';

  if (!faqItems.length) {
    return null;
  }

  return (
    <FAQSection dir={isRTL ? 'rtl' : 'ltr'}>
      <FAQTitle isRTL={isRTL}>{t('blog.faq.title')}</FAQTitle>
      <FAQDescription isRTL={isRTL}>
        {t('blog.faq.description')}
      </FAQDescription>

      <FAQContainer>
        {faqItems.map((item: FAQItem, index: number) => (
          <FAQItemComponent key={index}>
            <FAQQuestion
              onClick={() => toggleFAQ(index)}
              isOpen={expandedIndex === index}
              isRTL={isRTL}
            >
              <QuestionText isRTL={isRTL}>{item.question}</QuestionText>
              <FAQIcon isOpen={expandedIndex === index} isRTL={isRTL}>
                {expandedIndex === index ? '−' : '+'}
              </FAQIcon>
            </FAQQuestion>

            <AnimatePresence>
              {expandedIndex === index && (
                <FAQAnswer
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AnswerContent isRTL={isRTL}>{item.answer}</AnswerContent>
                </FAQAnswer>
              )}
            </AnimatePresence>
          </FAQItemComponent>
        ))}
      </FAQContainer>
    </FAQSection>
  );
};

// ============ STYLED COMPONENTS AVEC SUPPORT RTL ============

const FAQSection = styled.section<{ dir?: string }>`
  margin-top: var(--spacing-xxl);
  padding: var(--spacing-xl) 0;
  max-width: 800px;
  direction: ${props => props.dir || 'ltr'};
`;

const FAQTitle = styled.h2<{ isRTL?: boolean }>`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: var(--spacing-md);
  text-align: ${props => props.isRTL ? 'right' : 'left'};
`;

const FAQDescription = styled.p<{ isRTL?: boolean }>`
  font-size: 1rem;
  color: var(--gray);
  margin-bottom: var(--spacing-lg);
  text-align: ${props => props.isRTL ? 'right' : 'left'};
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FAQItemComponent = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow);
  }
`;

interface FAQQuestionProps {
  isOpen: boolean;
  isRTL?: boolean;
}

const FAQQuestion = styled.button<FAQQuestionProps>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg);
  background: ${props => props.isOpen ? 'var(--primary)' : 'var(--white)'};
  color: ${props => props.isOpen ? 'var(--white)' : 'var(--text-color)'};
  border: none;
  cursor: pointer;
  transition: var(--transition);
  font-size: 1rem;
  font-weight: 600;
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};

  &:hover {
    background: ${props => props.isOpen ? 'var(--primary-dark)' : 'var(--light)'};
  }
`;

const QuestionText = styled.span<{ isRTL?: boolean }>`
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  flex: 1;
`;

interface FAQIconProps {
  isOpen: boolean;
  isRTL?: boolean;
}

const FAQIcon = styled.span<FAQIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.2)' : 'var(--light)'};
  font-size: 1.5rem;
  line-height: 1;
  margin-${props => props.isRTL ? 'right' : 'left'}: var(--spacing-md);
  transition: var(--transition);
  flex-shrink: 0;
`;

const FAQAnswer = styled(motion.div)`
  overflow: hidden;
  background: var(--light);
  border-top: 1px solid var(--border-color);
`;

const AnswerContent = styled.p<{ isRTL?: boolean }>`
  padding: var(--spacing-lg);
  color: var(--text-color);
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: ${props => props.isRTL ? 'right' : 'left'};
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
`;

export default BlogFAQ;